import React, { useEffect, useRef } from "react"
import {
  ArrowUturnLeftIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline"
import { MODULE_LABELS } from "./registry"
import type { DestinationMatch, ResultGroup } from "./types"

interface Props {
  groups: ResultGroup[]
  /** Index into the flattened match list. */
  activeIndex: number
  /** Flattened matches, in the same order the groups render. */
  flattened: DestinationMatch[]
  onHover: (index: number) => void
  onSelect: (match: DestinationMatch) => void
  query: string
}

const Hint: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <li
    role="presentation"
    className="px-3 py-8 text-center text-sm text-neutral300"
  >
    {children}
  </li>
)

const GroupHeading: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <h3 className="px-1 pb-2 pt-4 text-xs font-semibold uppercase tracking-wider text-neutral700">
    {children}
  </h3>
)

const isWordChar = (char: string) => /[a-z0-9]/i.test(char)

/**
 * Character ranges of `label` to emphasise for `query`: each query term where
 * it starts a word. Word starts only, because that is how ranking matches a
 * label — highlighting "pay" inside "Repayments" would point at a reason the
 * row is not actually there for.
 */
export function highlightRanges(
  label: string,
  query: string
): Array<[number, number]> {
  const lower = label.toLowerCase()
  const terms = query
    .toLowerCase()
    .split(/[\s/&,-]+/)
    .filter(Boolean)

  const ranges: Array<[number, number]> = []
  for (const term of terms) {
    let from = lower.indexOf(term)
    while (from !== -1) {
      if (from === 0 || !isWordChar(lower[from - 1])) {
        ranges.push([from, from + term.length])
      }
      from = lower.indexOf(term, from + 1)
    }
  }

  ranges.sort((a, b) => a[0] - b[0])
  const merged: Array<[number, number]> = []
  for (const range of ranges) {
    const last = merged[merged.length - 1]
    if (last && range[0] <= last[1]) last[1] = Math.max(last[1], range[1])
    else merged.push([...range])
  }
  return merged
}

const Highlighted: React.FC<{
  text: string
  query: string
  isActive: boolean
}> = ({ text, query, isActive }) => {
  const ranges = highlightRanges(text, query)
  if (ranges.length === 0) return <>{text}</>

  const parts: React.ReactNode[] = []
  let cursor = 0
  ranges.forEach(([start, end]) => {
    if (start > cursor) parts.push(text.slice(cursor, start))
    parts.push(
      <mark
        key={start}
        className={`bg-transparent ${
          isActive ? "text-white underline" : "text-primary500"
        }`}
      >
        {text.slice(start, end)}
      </mark>
    )
    cursor = end
  })
  if (cursor < text.length) parts.push(text.slice(cursor))
  return <>{parts}</>
}

const rowClass = (isActive: boolean) =>
  `flex min-h-[56px] w-full items-center gap-3 rounded-lg px-3 py-2 text-left ${
    isActive ? "bg-primary500" : "bg-white"
  }`

const EnterGlyph: React.FC<{ isActive: boolean }> = ({ isActive }) =>
  isActive ? (
    <ArrowUturnLeftIcon className="h-5 w-5 shrink-0 text-white" />
  ) : null

/**
 * The results list.
 *
 * The active row is filled rather than tinted, so the keyboard position is
 * unmistakable at a glance, and carries the Enter glyph to say what Enter does.
 */
export const GlobalSearchResults: React.FC<Props> = ({
  groups,
  activeIndex,
  flattened,
  onHover,
  onSelect,
  query,
}) => {
  const trimmed = query.trim()
  const listRef = useRef<HTMLUListElement>(null)

  // Keep the keyboard position in view as the arrows walk past the fold.
  useEffect(() => {
    const active = listRef.current?.querySelector<HTMLElement>(
      `#global-search-option-${activeIndex}`
    )
    active?.scrollIntoView?.({ block: "nearest" })
  }, [activeIndex])

  const body = () => {
    if (trimmed.length === 0) {
      return (
        <Hint>
          Search for a page — try &ldquo;exit&rdquo; or &ldquo;payroll&rdquo;.
        </Hint>
      )
    }

    if (groups.length === 0) {
      // No "no access" rows and no zero counts: absence of permission has to
      // look the same as absence of data.
      return <Hint>No pages match &ldquo;{trimmed}&rdquo;.</Hint>
    }

    return groups.map((group) => (
      <li key={group.module}>
        <GroupHeading>{MODULE_LABELS[group.module]}</GroupHeading>
        <ul className="space-y-1">
          {group.matches.map((match) => {
            const index = flattened.indexOf(match)
            const isActive = index === activeIndex
            const { label, route } = match.destination

            return (
              <li key={match.destination.id}>
                <button
                  type="button"
                  role="option"
                  id={`global-search-option-${index}`}
                  aria-selected={isActive}
                  onMouseMove={() => onHover(index)}
                  onClick={() => onSelect(match)}
                  className={rowClass(isActive)}
                >
                  <DocumentTextIcon
                    className={`h-5 w-5 shrink-0 ${
                      isActive ? "text-white" : "text-neutral300"
                    }`}
                  />
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span
                      data-label={label}
                      className={`truncate text-sm font-medium ${
                        isActive ? "text-white" : "text-neutral700"
                      }`}
                    >
                      <Highlighted
                        text={label}
                        query={trimmed}
                        isActive={isActive}
                      />
                    </span>
                    <span
                      className={`truncate text-xs ${
                        isActive ? "text-white/80" : "text-neutral300"
                      }`}
                    >
                      {route}
                    </span>
                  </span>
                  <EnterGlyph isActive={isActive} />
                </button>
              </li>
            )
          })}
        </ul>
      </li>
    ))
  }

  return (
    <ul
      ref={listRef}
      role="listbox"
      aria-label="Search results"
    >
      {body()}
    </ul>
  )
}

/**
 * The handover to HandHold. Rendered by the dialog below the scrolling
 * results rather than at the end of them, so it stays in view at any result
 * count, the way a command palette pins its suggestions above the footer.
 *
 * `index` is one past the last match: the handover is the final stop of the
 * same arrow-key run that walks the results, not a separate control the
 * keyboard cannot reach. Its own listbox, since it sits outside the results'.
 */
export const HandholdSuggestion: React.FC<{
  index: number
  activeIndex: number
  query: string
  onHover: (index: number) => void
  onSelect: () => void
}> = ({ index, activeIndex, query, onHover, onSelect }) => {
  const trimmed = query.trim()
  const isActive = activeIndex === index

  return (
    <div
      id="global-search-suggestions"
      role="listbox"
      aria-label="Suggestions"
    >
      <GroupHeading>Suggestions</GroupHeading>
      <button
        type="button"
        role="option"
        id={`global-search-option-${index}`}
        aria-selected={isActive}
        onMouseMove={() => onHover(index)}
        onClick={onSelect}
        className={rowClass(isActive)}
      >
        <ChatBubbleLeftRightIcon
          className={`h-5 w-5 shrink-0 ${
            isActive ? "text-white" : "text-primary500"
          }`}
        />
        <span className="flex min-w-0 flex-1 flex-col">
          <span
            className={`truncate text-sm font-medium ${
              isActive ? "text-white" : "text-neutral700"
            }`}
          >
            {trimmed.length === 0 ? (
              "Get help with PaidGuide"
            ) : (
              <>Ask PaidGuide about &ldquo;{trimmed}&rdquo;</>
            )}
          </span>
          <span
            className={`truncate text-xs ${
              isActive ? "text-white/80" : "text-neutral300"
            }`}
          >
            {trimmed.length === 0 ? "Open guide" : "Start a guide"}
          </span>
        </span>
        <EnterGlyph isActive={isActive} />
      </button>
    </div>
  )
}
